/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import {
	AGENT_AVATAR_CLIP_PATH,
	AgentAvatar,
} from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';

import type { SuggestionProfile } from '../../aiSuggestionsPluginType';

/**
 * A profile in the avatar stack. Only agents appear here — human authors are deliberately
 * excluded. A slot with no `profile` means the suggestion had no `createdByAgent`, and renders the
 * generic Rovo hex logo instead.
 */
export type SuggestionAvatarProfile = { profile?: SuggestionProfile };

// Matches the 2px box-shadow ring `@atlaskit/avatar` draws by default, so this stack lines up with
// avatars elsewhere. `color.border.inverse` reads as white in light mode against the avatar's bold
// background, and flips to a dark ring in dark mode for the same contrast.
const avatarRingColor = token('color.border.inverse');

const styles = cssMap({
	// Sized to match `AgentAvatar`'s own `xxsmall` footprint, so the manually-drawn ring below
	// sits flush against it.
	avatarRingWrapper: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: token('space.200'),
		width: token('space.200'),
	},
	// Hand-drawn hexagon ring for agents — `AgentAvatar`'s own `showBorder` inset is too thin at
	// `xxsmall`. `inset: -2px` centers this via exact pixel math off `avatarRingWrapper`'s own
	// box, avoiding the sub-pixel drift a transform-based centering approach could introduce.
	agentAvatarRing: {
		position: 'absolute',
		zIndex: 0,
		inset: '-2px',
		backgroundColor: avatarRingColor,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	avatarRingContent: {
		position: 'relative',
		zIndex: 1,
		// Explicit size + centering forces the content dead-center regardless of its own box
		// model. `fontSize: 0` strips any line-box height it could otherwise inherit, which
		// would offset it via baseline alignment instead of true centering.
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: token('space.200'),
		width: token('space.200'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Not real text; collapses the phantom inline line-box, no typography token applies.
		fontSize: 0,
	},
});

/**
 * One agent avatar's ring + content — the specific agent, or the generic Rovo fallback when the
 * suggestion has no agent profile. Used by `SuggestionAvatarIndicator` to render each entry in the
 * stack.
 */
export function SuggestionAvatar({ profile }: SuggestionAvatarProfile): JSX.Element {
	return (
		<span css={styles.avatarRingWrapper}>
			<span css={styles.agentAvatarRing} />
			<span css={styles.avatarRingContent}>
				{profile ? (
					<AgentAvatar
						imageUrl={profile.picture?.trim() || undefined}
						agentIdentityAccountId={profile.accountId}
						name={profile.name}
						label={profile.name}
						size="xxsmall"
						showBorder={false}
					/>
				) : (
					<RovoHexIcon size="xxsmall" />
				)}
			</span>
		</span>
	);
}
